import { Component } from '@theme/component';

import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';

import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) {
        this.#onQuantityChange(event);
      }
    },
    300
  );

  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /** @type {Promise<string> | null} */
  #pendingExternalCartRefresh = null;

  #externalCartRefreshQueued = false;

  /**
   * True when the event was dispatched from outside this cart-items-component.
   *
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];

        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });

          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Event promise rejected:', error);
        }
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    if (!this.#isExternalCartUpdate(event)) return;

    event.promise
      ?.then(({ detail }) => {
        const sections =
          /** @type {Record<string, string> | undefined} */ (
            detail?.sections
          );

        const sectionsHtml = sections?.[this.sectionId];

        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Event promise rejected:', error);
        }
      });
  };

  /**
   * Handles Monk / third-party cart mutations.
   *
   * Monk dispatches `cart:refresh` after it adds or removes an offer line.
   * Horizon normally uses Shopify standard events instead, so without this
   * bridge the server cart can be correct while the drawer still shows
   * the previous hydrated section.
   */
  #handleExternalCartRefresh = () => {
    this.#externalCartRefreshQueued = true;
    this.#renderExternalCartState();
  };

  async #renderExternalCartState() {
    if (this.#pendingExternalCartRefresh) {
      return this.#pendingExternalCartRefresh;
    }

    this.#pendingExternalCartRefresh = (async () => {
      do {
        this.#externalCartRefreshQueued = false;

        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      } while (this.#externalCartRefreshQueued);
    })()
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] External cart refresh failed:', error);
        }
      })
      .finally(() => {
        this.#pendingExternalCartRefresh = null;

        if (this.#externalCartRefreshQueued) {
          this.#renderExternalCartState();
        }
      });

    return this.#pendingExternalCartRefresh;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );

    document.addEventListener(
      StandardEvents.cartDiscountUpdate,
      this.#handleDiscountUpdate
    );

    document.addEventListener(
      StandardEvents.cartNoteUpdate,
      this.#handleNoteUpdate
    );

    document.addEventListener(
      'cart:refresh',
      this.#handleExternalCartRefresh
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate
    );

    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );

    document.removeEventListener(
      StandardEvents.cartDiscountUpdate,
      this.#handleDiscountUpdate
    );

    document.removeEventListener(
      StandardEvents.cartNoteUpdate,
      this.#handleNoteUpdate
    );

    document.removeEventListener(
      'cart:refresh',
      this.#handleExternalCartRefresh
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) {
      return;
    }

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const sourceRow =
      event.target instanceof Element
        ? event.target.closest('[data-key]')
        : null;

    const lineKey =
      sourceRow instanceof HTMLElement
        ? sourceRow.dataset.key || ''
        : this.refs.cartItemRows[line - 1]?.dataset.key || '';

    this.updateQuantity({
      line,
      lineKey,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent =
      /** @type {TextComponent | undefined} */ (
        lineItemRow.querySelector('text-component')
      );

    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   *
   * @param {number} line
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const lineKey = cartItemRowToRemove.dataset.key || '';

    this.updateQuantity({
      line,
      lineKey,
      quantity: 0,
      action: 'clear',
    });

    const rowsToRemove = [
      cartItemRowToRemove,

      ...this.refs.cartItemRows.filter(
        (row) =>
          row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];

    const isEmptyCart =
      rowsToRemove.length == this.refs.cartItemRows.length;

    if (isEmptyCart) {
      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) {
        return remove();
      }

      row.style.setProperty(
        '--row-height',
        `${row.clientHeight}px`
      );

      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   *
   * @param {Object} config
   * @param {number} config.line
   * @param {string} [config.lineKey]
   * @param {number} config.quantity
   * @param {string} config.action
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker =
      cartPerformance.createStartingMarker(
        `${config.action}:user-action`
      );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const sectionsToUpdate =
      new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        sectionsToUpdate.add(
          item.dataset.sectionId
        );
      }
    });

    /*
     * Use Shopify's stable cart line key instead of the numeric line position.
     *
     * Monk can insert/remove gift lines asynchronously, which can change
     * cart ordering. A numeric line can therefore point at a different
     * product by the time Shopify processes the request.
     */
    const lineId =
      config.lineKey ||
      this.refs.cartItemRows[line - 1]?.dataset.key ||
      '';

    if (!lineId) {
      this.#enableCartItems();
      throw new Error('Cart line key missing');
    }

    const body = JSON.stringify({
      id: lineId,
      quantity,
      sections:
        Array.from(sectionsToUpdate).join(','),
      sections_url:
        window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action:
          config.action === 'change' &&
          quantity > 0
            ? 'update'
            : 'remove',

        context: 'cart',

        lines: [
          {
            id: lineId,
            quantity,
          },
        ],

        promise:
          deferredUpdatePromise.promise,
      })
    );

    fetch(
      `${Theme.routes.cart_change_url}`,
      fetchConfig('json', { body })
    )
      .then((response) => {
        return response.text();
      })

      .then((responseText) => {
        const parsedResponseText =
          JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(
            line,
            parsedResponseText
          );

          deferredUpdatePromise.reject(
            new Error(
              parsedResponseText.errors
            )
          );

          return;
        }

        const newSectionHTML =
          new DOMParser().parseFromString(
            parsedResponseText.sections[
              this.sectionId
            ],
            'text/html'
          );

        const newCartHiddenItemCount =
          newSectionHTML.querySelector(
            '[ref="cartItemCount"]'
          )?.textContent;

        const newCartItemCount =
          newCartHiddenItemCount
            ? parseInt(
                newCartHiddenItemCount,
                10
              )
            : 0;

        this.#updateQuantitySelectors(
          parsedResponseText
        );

        deferredUpdatePromise.resolve({
          cart:
            CartLinesUpdateEvent.createCartFromAjaxResponse(
              parsedResponseText
            ),

          detail: {
            sections:
              parsedResponseText.sections,

            items:
              parsedResponseText.items,

            itemCount:
              newCartItemCount,

            source:
              'cart-items-component',

            didError:
              false,
          },
        });

        morphSection(
          this.sectionId,
          parsedResponseText.sections[
            this.sectionId
          ],
          {
            mode:
              this.isDrawer
                ? 'hydration'
                : 'full',
          }
        );

        this.#updateCartQuantitySelectorButtonStates();
      })

      .catch((error) => {
        console.error(error);

        deferredUpdatePromise.reject(
          error
        );

        this.dispatchEvent(
          new CartErrorEvent({
            error:
              error?.message ||
              'Failed to update cart',

            code:
              'SERVICE_UNAVAILABLE',
          })
        );
      })

      .finally(() => {
        this.#enableCartItems();

        cartPerformance.measureFromMarker(
          cartPerformaceUpdateMarker
        );
      });
  }

  /**
   * Handles the cart error.
   *
   * @param {number} line
   * @param {Object} parsedResponseText
   * @param {string} parsedResponseText.errors
   */
  #handleCartError = (
    line,
    parsedResponseText
  ) => {
    const quantitySelector =
      this.refs.quantitySelectors[line - 1];

    const quantityInput =
      quantitySelector?.querySelector(
        'input'
      );

    if (!quantityInput) {
      throw new Error(
        'Quantity input not found'
      );
    }

    quantityInput.value =
      quantityInput.defaultValue;

    const cartItemError =
      this.refs[
        `cartItemError-${line}`
      ];

    const cartItemErrorContainer =
      this.refs[
        `cartItemErrorContainer-${line}`
      ];

    if (
      !(cartItemError instanceof HTMLElement)
    ) {
      throw new Error(
        'Cart item error not found'
      );
    }

    if (
      !(
        cartItemErrorContainer instanceof
        HTMLElement
      )
    ) {
      throw new Error(
        'Cart item error container not found'
      );
    }

    cartItemError.textContent =
      parsedResponseText.errors;

    cartItemErrorContainer.classList.remove(
      'hidden'
    );

    this.dispatchEvent(
      new CartErrorEvent({
        error:
          parsedResponseText.errors ||
          'Cart update failed',

        code:
          'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections =
          detail?.sections;

        const cartItemsHtml =
          sections?.[this.sectionId];

        const wasEmptyCartDrawer =
          this.isDrawer &&
          this.querySelector(
            '[data-cart-drawer-empty]'
          ) !== null;

        /** @type {'hydration' | 'full'} */
        const mode =
          this.isDrawer
            ? 'hydration'
            : 'full';

        const morphOptions = {
          mode,
          injectStylesheet:
            wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys =
            new Set(
              this.refs.cartItemRows?.map(
                (row) =>
                  row.dataset.key
              ) ?? []
            );

          if (wasEmptyCartDrawer) {
            startViewTransition(
              () => {
                morphSection(
                  this.sectionId,
                  cartItemsHtml,
                  morphOptions
                );
              },
              ['fill-cart-drawer']
            );
          } else {
            await morphSection(
              this.sectionId,
              cartItemsHtml,
              morphOptions
            );
          }

          if (
            !wasEmptyCartDrawer &&
            !prefersReducedMotion()
          ) {
            for (
              const row of
              this.refs.cartItemRows ?? []
            ) {
              if (
                !existingKeys.has(
                  row.dataset.key
                )
              ) {
                row.classList.add(
                  'adding'
                );

                onAnimationEnd(
                  row,
                  () =>
                    row.classList.remove(
                      'adding'
                    )
                );
              }
            }
          }

          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(
            this.sectionId,
            {
              cache: false,
              ...morphOptions,
            }
          );
        }
      })

      .catch((error) => {
        if (
          error?.name !==
          'AbortError'
        ) {
          console.warn(
            '[cart-items] Event promise rejected:',
            error
          );
        }
      });
  };

  #disableCartItems() {
    this.classList.add(
      'cart-items-disabled'
    );
  }

  #enableCartItems() {
    this.classList.remove(
      'cart-items-disabled'
    );
  }

  /**
   * Updates quantity selectors for all matching variants.
   *
   * @param {Object} updatedCart
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items]
   */
  #updateQuantitySelectors(
    updatedCart
  ) {
    if (!updatedCart.items) return;

    for (
      const item of
      updatedCart.items
    ) {
      const variantId =
        item.variant_id.toString();

      const selectors =
        document.querySelectorAll(
          `quantity-selector-component[data-variant-id="${variantId}"]`
        );

      for (
        const selector of
        selectors
      ) {
        const input =
          selector.querySelector(
            'input[data-cart-quantity]'
          );

        if (!input) continue;

        input.setAttribute(
          'data-cart-quantity',
          item.quantity.toString()
        );

        if (
          'updateCartQuantity' in
            selector &&
          typeof selector
            .updateCartQuantity ===
            'function'
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (
      const selector of
      document.querySelectorAll(
        'cart-quantity-selector-component'
      )
    ) {
      /** @type {any} */ (
        selector
      ).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) {
      return this.#pendingCartFetch;
    }

    this.#pendingCartFetch =
      (async () => {
        const response = await fetch(
          `${Theme.routes.cart_url}.json`,
          {
            headers: {
              Accept:
                'application/json',
            },

            credentials:
              'same-origin',
          }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch cart: ${response.status} ${response.statusText}`
          );
        }

        const data =
          await response.json();

        return data;
      })().finally(() => {
        this.#pendingCartFetch = null;
      });

    return this.#pendingCartFetch;
  }

  get sectionId() {
    const { sectionId } =
      this.dataset;

    if (!sectionId) {
      throw new Error(
        'Section id missing'
      );
    }

    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (
  !customElements.get(
    'cart-items-component'
  )
) {
  customElements.define(
    'cart-items-component',
    CartItemsComponent
  );
}